import type { store as StoreType } from '@/store'
import { computed, type ComputedRef, inject } from 'vue'
import type { Store } from 'vuex'

type StoreType2 = typeof StoreType
type AppStore = Store<StoreType2>
type StoreModuleState = StoreType2['modules']
type StoreModule = keyof StoreModuleState
type ModuleState<T extends StoreModule> = StoreModuleState[T]['state']
type StoreModuleFields<T extends StoreModule> = keyof ModuleState<T>
type ModuleGetters = AppStore['getters']

type ComputedModuleState<T extends StoreModule> = ComputedRef<ModuleState<T>>

type ComputedGetters = ComputedRef<ModuleGetters>

type Fields<T extends StoreModule> = Record<
  StoreModuleFields<T>,
  ComputedRef<ModuleState<T>>
>

export const useStore = () => {
  const store = inject<AppStore | null>('store', null)
  if (!store) {
    throw new Error(
      'Store not found. Ensure that the store is properly provided.'
    )
  }

  return store
}

export const useState = <T extends StoreModule>(
  moduleName: T,
  fields: StoreModuleFields<T>[]
): Record<'moduleState', ComputedModuleState<T>> & Fields<T> => {
  const store = useStore()

  const moduleState = computed(
    () => (store?.state as Record<string, any>)?.[moduleName]
  )

  const fieldStates = {} as Fields<T>

  fields.forEach((field) => {
    fieldStates[field] = computed(() => moduleState.value[field])
  })

  return { moduleState: moduleState as ComputedModuleState<T>, ...fieldStates }
}

export const useGetters = <T extends StoreModule>(
  moduleName: T,
  fields: ModuleGetters[]
): Record<'getters', ComputedGetters> => {
  const store = useStore()
  const getters = computed(() => store?.getters)

  const fieldGetters: Record<string, ComputedRef<any>> = {}

  fields.forEach((field) => {
    fieldGetters[field] = computed(() => getters.value[field])
  })
  return { getters, ...fieldGetters }
}
